import React, { useCallback, useEffect, useMemo } from 'react';
import { useNavigate, Outlet, useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

// Components
import Tabs from '../../../../components/display/Tabs';

// Types
import { tabsType } from '../../../../state/reducers/ui/tabs';
import { OrderType } from '../../../../types/orders';
import { type SurveillanceRoute } from '../../../../types/surveillanceRoute';

// Apollo
import { client } from '../../../../gql/client';
import { GET_SURVEILLANCE_ROUTE_BY_ID } from '../../../../gql/queries/surveillanceRoute';
import { GET_ORDERS_BY_IDS_SERVER } from '../../../../gql/queries/orders/orders';

// Redux
import {
  roidOTSelected,
  roidOTSelectedReset,
} from '../../../../state/actions/ui/roidOTSelectedGeneralView';

// Utils
import { buildRoidFallback } from '../../../../utils/surveillanceRoute';

// Offline
import { useRevalidateOnSync } from '../../../../offline/useRevalidateOnSync';

// Styles
import { PageContainer } from './styles';

// ═══════════════════════════════════════════════════════════════════
// TAB PATH HELPERS
// ═══════════════════════════════════════════════════════════════════

const TAB_PATHS_BASE = '/surveillance-route';

// Multimedia dejó de ser una tab (feedback usuario 2026-07-08): "Ver fotos"
// desde ShowOT en la tab "Órdenes asociadas" navega a la ruta standalone
// `/surveillance-route/:routeId/multimedia` (hermana de este wrapper de
// tabs, fuera de él — espejo del patrón /work-orders/:id/multimedia). Solo
// quedan 2 tabs.
const buildTabPaths = (routeId: string): string[] => [
  `${TAB_PATHS_BASE}/${routeId}`,
  `${TAB_PATHS_BASE}/${routeId}/child-orders`,
];

const getIndexFromPath = (pathname: string, tabPaths: string[]): number => {
  const normalized = pathname.replace(/\/$/, '');
  const idx = tabPaths.indexOf(normalized);
  return idx >= 0 ? idx : 0;
};

// ═══════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════

export default function RouteDetailedPage(): JSX.Element {
  const { t } = useTranslation('pages');
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { routeId } = useParams<{ routeId: string }>();

  // Al completar la sincronización offline (p.ej. "Iniciar recorrido" encolado
  // sin red → replay al reconectar), revalida el loader del detalle para que
  // la tarjeta refleje el estado REAL del servidor. Sin esto, el detalle se
  // quedaba en "Abierta" pese a que la ruta ya estaba IN_PROGRESS (el timer,
  // que lee GetActiveSurveillanceRoutes en vivo, sí lo mostraba — inconsistencia
  // reportada en pruebas de campo C-25). Espejo de WorkOrderDetailed/
  // CampaignDetailed, que ya usan este hook. Cubre las 2 tabs (preview +
  // órdenes) porque vive en el padre.
  useRevalidateOnSync();

  const tabPaths = useMemo(
    () => buildTabPaths(routeId || ''),
    [routeId]
  );

  // URL is the source of truth for the active tab on mount/reload
  const urlIndex = getIndexFromPath(location.pathname, tabPaths);

  // V4 iter7+ (2026-05-07) — dispatch roidOTSelected con el ROID canónico
  // del SR para que el Breadcrumbs reemplace el Mongo ObjectId crudo
  // (`/surveillance-route/:id`) por la etiqueta `Su.NNNNNNN.NNNN.02` y
  // arregle el pathAbsolute de los tabs hijos (child-orders).
  // Se ejecuta en el contexto del padre, así cubre las 2 tabs (preview +
  // child-orders) independientemente de cuál esté activa. Multimedia ya NO
  // es una tab (2026-07-08) — vive fuera de este wrapper y replica su
  // propio dispatch (ver RouteMultimediaPage).
  useEffect(() => {
    if (!routeId) return;
    let cancelled = false;
    (async () => {
      try {
        const sr = client.readQuery<{ surveillanceRoute: SurveillanceRoute | null }>({
          query: GET_SURVEILLANCE_ROUTE_BY_ID,
          variables: { id: routeId },
        })?.surveillanceRoute;
        if (cancelled || !sr) return;

        let roid = '';
        const orderId = (sr as any).orderId ?? sr.orderIDs?.[0];
        if (orderId) {
          try {
            const ordersRes = await client.query({
              query: GET_ORDERS_BY_IDS_SERVER,
              variables: { ids: [orderId] },
              fetchPolicy: 'cache-first',
            });
            roid = ordersRes.data?.ordersByIds?.[0]?.roid || '';
          } catch {
            // Order fetch falló — caemos al ROID canónico desde indexExpedition.
          }
        }
        if (!roid) roid = buildRoidFallback(sr);
        if (cancelled || !roid) return;
        dispatch(roidOTSelected(OrderType.SurveillanceRoute, roid, sr.id));
      } catch {
        // Cache miss — el RoutePreviewPage también dispatcha cuando carga.
      }
    })();
    return () => {
      cancelled = true;
      // Reset al desmontar para no contaminar otros listings.
      dispatch(roidOTSelectedReset());
    };
  }, [routeId, dispatch]);

  const dataTab = useMemo(
    () => [
      { label: t('surveillance.preview') },
      { label: t('surveillance.childOrders.title') },
    ],
    [t]
  );

  // Navigate when the selected tab changes (via click).
  // On mount/reload this is a no-op because the URL already matches.
  const handleTabChange = useCallback(
    (newIndex: number) => {
      const currentPath = location.pathname.replace(/\/$/, '');
      const targetPath = tabPaths[newIndex] || tabPaths[0];

      if (currentPath !== targetPath) {
        navigate(targetPath);
      }
    },
    [location.pathname, navigate, tabPaths]
  );

  return (
    <PageContainer>
      <Tabs
        data={dataTab}
        typeTab={tabsType.SURVEILLANCE_ROUTE}
        defaultIndex={urlIndex}
        setSelectedIndexTab={handleTabChange}
      />
      <Outlet />
    </PageContainer>
  );
}
